import { useEffect, useState } from "react";
import { collectionGroup, getDocs, query } from "firebase/firestore";
import { db } from 'src/utils/firebase';
import { styled } from "@mui/material";
import { useNavigate } from "react-router-dom";

interface RowData {
    [key: string]: any;
}

const TableWrapper = styled("div")(() => ({
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f3f4f6",
    padding: "24px",
}));

const TableContainer = styled("div")(() => ({
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    border: "1px solid #dcd4d4",
    overflow: "hidden",
    padding: "16px 20px",
}));

const Title = styled("h1")(() => ({
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "12px",
    textAlign: "center",
    color: "#111827",
}));

const StyledTable = styled("table")(() => ({
    borderCollapse: "collapse",
    borderRadius: "30px"
}));

const Th = styled("th")(() => ({
    cursor: "pointer",
    padding: "8px 14px",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    border: "1px solid #000000",
    backgroundColor: "#f9fafb",
}));

const Td = styled("td")(() => ({
    padding: "8px 14px",
    fontSize: "13px",
    color: "#1f2937",
    border: "1px solid #000000",
}));

const columnMap: Record<string, string> = {
    client_name: "Client Name",
    start_timestamp: "Start Date",
    end_timestamp: "End Date",
    duplicated_at: "Duplicated At",
    signed_timestamp: "Signed On",
    lnp_signed_by: "Signed By",
    agreement_name: "Agreement Name"
};

const end_timestamp_key = Object.keys(columnMap).indexOf('end_timestamp');

export default function FirestoreTablePage() {
    const [data, setData] = useState<RowData[]>([]);
    const [sortKey, setSortKey] = useState<string>("");
    const [sortAsc, setSortAsc] = useState<boolean>(true);
    const navigate = useNavigate();


    useEffect(() => {
        const fetchData = async () => {
            try {
                const q = query(collectionGroup(db, "agreements"));
                const snapshot = await getDocs(q);

                const rows: RowData[] = snapshot.docs.map((doc) => ({
                    // id: doc.id,
                    ...doc.data(),
                }));

                setData(rows);
            } catch (err) {
                console.error(err);
            }
        };

        fetchData();
    }, []);

    const handleSort = (key: string) => {
        if (sortKey === key) {
            setSortAsc(!sortAsc);
        } else {
            setSortKey(key);
            setSortAsc(true);
        }
    };

    const sortedData = [...data].sort((a, b) => {
        if (!sortKey) return 0;

        const aVal = a[sortKey];
        const bVal = b[sortKey];

        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === "number" && typeof bVal === "number") {
            return sortAsc ? aVal - bVal : bVal - aVal;
        }

        return sortAsc
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
    });

    const columns = Object.keys(columnMap) as (keyof typeof columnMap)[];

    function getRowColor(months: number): string {
        const m = Math.max(-6, Math.min(months, 12));
        const base_intensity = 0.8

        // NEGATIVE: expired
        if (m < 0) {
            return `rgba(180, 140, 255, ${0.15 + 1 * base_intensity - 0.5})`;
        }

        // RED: 0–3 months
        if (m <= 3) {
            const intensity = 1 - m / 3;
            return `rgba(255, 160, 160, ${0.15 + intensity * base_intensity})`;
        }

        // YELLOW: 3–6 months
        if (m <= 6) {
            const intensity = 1 - (m - 3) / 3;
            return `rgba(255, 230, 160, ${0.15 + intensity * base_intensity})`;
        }

        // GREEN: > 6 months
        const intensity = Math.min((m - 6) / 6, 1);
        return `rgba(180, 240, 180, ${0.15 + intensity * base_intensity})`;
    }

    function getMonthsDifference(dateString: string): number {
        const inputDate = new Date(dateString);
        const now = new Date();

        let months =
            (inputDate.getFullYear() - now.getFullYear()) * 12 +
            (inputDate.getMonth() - now.getMonth());

        if (now.getDate() > inputDate.getDate()) {
            months -= 1;
        }

        return Math.floor(months);
    }

    function redirectToRowAgreement(agreement_row: any) {

        const crypto_order = {
            ...agreement_row, auth_id: agreement_row?.client_auth_id, name: agreement_row?.client_name
        }

        navigate("/dashboards/client-agreements", {
            state: {
                cryptoOrder: crypto_order,
            }
        });
    }

    return (
        <TableWrapper>
            <TableContainer>
                <Title>All Agreements Table</Title>

                <StyledTable>
                    <thead>
                        <tr>
                            {columns.map((col: string) => (
                                <Th key={col} onClick={() => handleSort(col)}>
                                    {columnMap[col]}
                                    {sortKey === col && (
                                        <span style={{ marginLeft: 6, fontSize: 11 }}>
                                            {sortAsc ? "▲" : "▼"}
                                        </span>
                                    )}
                                </Th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {sortedData.map((row: any) => {
                            const groupKey = String(row[columns[end_timestamp_key]] ?? "");
                            const months = getMonthsDifference(groupKey);
                            const bgColor = getRowColor(months);

                            return (
                                <tr key={row.id ?? `${groupKey}`} style={{ backgroundColor: bgColor }} onClick={() => redirectToRowAgreement(row)}>
                                    {columns.map((col: string) => (
                                        <Td key={col}>
                                            {row[col] != null ? String(row[col]) : "-"}
                                        </Td>
                                    ))}
                                </tr>
                            );
                        })}

                        {sortedData.length === 0 && (
                            <tr>
                                <Td colSpan={columns.length} style={{ textAlign: "center" }}>
                                    Error no data loaded!
                                </Td>
                            </tr>
                        )}
                    </tbody>
                </StyledTable>
            </TableContainer>
        </TableWrapper>
    );
}
